import { PropsWithChildren, useRef } from 'react';

import { styled } from '@mui/material';
import * as Sentry from '@sentry/react';

import { Scroller, toDsVarsDefinition, Box } from '@cast/design-system';

import {
  AIAssistantSidebar,
  useIsOpsPilotEnabled,
} from '@cast/features-organization-shared-opspilot';
import {
  AccountOnHoldHeader,
  ESCALATION_HEADER_HEIGHT,
  EscalationPrivilegeHeader,
} from 'components/sticky-headers';
import { IS_DEV } from '@cast/constants';
import { PortalSlot } from 'core/portals';
import { OnboardingWizardButton } from 'features/onboarding-wizard';
import { SecondarySidebarState } from 'main-layout-op/_types';
import { mainLayoutCssVars } from 'main-layout-op/cssVars';

import { Header } from './_components/Header';
import { ProductPickerDrawer } from './_components/ProductPickerDrawer';
import { Sidebar } from './_components/sidebar/Sidebar';
import {
  MainLayoutProvider,
  useMainLayout,
} from './_providers/MainLayoutProvider';
import { calculateGridSidebarWidth } from './_utils';

type MainLayoutProps = PropsWithChildren<{
  noMenu?: boolean;
  noSidebar?: boolean;
}>;

type PageLayoutProps = {
  sidebarState: SecondarySidebarState;
  noSidebar: boolean;
};

// Max content width (1868px) + horizontal padding (2 × 24px)
const CONTENT_BOX_MAX_WIDTH = 1916;

const PageLayout = styled('div', {
  target: 'PageLayout-root',
  shouldForwardProp: (propName: PropertyKey) =>
    !['sidebarState', 'noSidebar'].includes(propName as string),
})<PageLayoutProps>(({ noSidebar, sidebarState }) => {
  const gridSidebarWidth = calculateGridSidebarWidth(noSidebar, sidebarState);
  return [
    {
      '--escalation-header-height': '0px',
      [`&:has(.escalation-header)`]: {
        '--escalation-header-height': `${ESCALATION_HEADER_HEIGHT}px`,
      },
      display: 'grid',
      minHeight: '100dvh',
      gridTemplateColumns: `[sidebar] ${gridSidebarWidth}px [main] minmax(0, 1fr)`,
      gridTemplateRows:
        '[escalation-header] max-content [toolbar] max-content [global-header] max-content [sticky-header] max-content [content] 1fr [gap] 40px [footer] max-content',
    },
  ];
});

const MainLayoutInside = ({ noMenu, children }: MainLayoutProps) => {
  const { secondarySidebarState, noSidebar } = useMainLayout();
  const { opsPilotStatus } = useIsOpsPilotEnabled();
  const sidebarWidth = calculateGridSidebarWidth(
    noSidebar,
    secondarySidebarState
  );

  return (
    <>
      <Scroller
        sx={{
          height: '100dvh',
          ...mainLayoutCssVars.setValues(
            {
              contentWidth: `min(calc(100vw - ${sidebarWidth}px), ${CONTENT_BOX_MAX_WIDTH}px)`,
            },
            true
          ),
        }}
        options={{
          overflow: { x: 'hidden' },
          scrollbars: { autoHide: 'never' },
        }}
      >
        <PageLayout
          noSidebar={noSidebar ?? false}
          sidebarState={secondarySidebarState}
          style={toDsVarsDefinition({ inset: { pl: '24px', pr: '24px' } })}
        >
          {!noSidebar && <Sidebar />}

          <Header noSidebar={noSidebar} noMenu={noMenu} />

          <EscalationPrivilegeHeader
            sx={{
              gridColumn: '1 / -1',
              gridRow: 'escalation-header',
            }}
          />

          <AccountOnHoldHeader
            sx={{
              gridRow: 'global-header',
            }}
          />

          <PortalSlot
            sx={{
              gridColumn: 'main',
              gridRow: 'sticky-header',
            }}
            portal="PAGE_HEADER"
          />

          <Box
            component="main"
            position="relative"
            width="100%"
            minWidth={0}
            maxWidth={mainLayoutCssVars.getters.contentWidth()}
            margin="0 auto"
            gridColumn="main"
            gridRow="content"
            px={mainLayoutCssVars.getters.horizontalPadding()}
            py={16}
          >
            {children}
          </Box>

          <PortalSlot
            sx={{
              gridColumn: 'main',
              gridRow: 'footer',
              bottom: 0,
              position: 'sticky',
              isolation: 'isolate',
              zIndex: 1,
            }}
            portal="PAGE_FOOTER"
          />
        </PageLayout>
      </Scroller>
      {opsPilotStatus === 'on' && <AIAssistantSidebar />}
      {!noSidebar && <ProductPickerDrawer />}
      <OnboardingWizardButton />
    </>
  );
};

export const MainLayout = (props: MainLayoutProps) => {
  const { isInsideMainLayout } = useMainLayout();
  const sentryMessagePushed = useRef<boolean>(undefined);

  if (isInsideMainLayout) {
    const message = 'MainLayout rendered inside MainLayout';
    if (IS_DEV) {
      throw new Error(message);
    }
    if (!sentryMessagePushed.current) {
      Sentry.captureMessage(message);
      sentryMessagePushed.current = true;
    }
    return <>{props.children}</>;
  }

  return (
    <MainLayoutProvider noSidebar={props.noSidebar ?? false}>
      <MainLayoutInside {...props} />
    </MainLayoutProvider>
  );
};
